import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class CryptoService {
  private readonly ivLength = 12;

  constructor() {}

  /** Derive an AES key from a password and salt */
  async deriveKey(password: string, salt: string): Promise<CryptoKey> {
    const enc = new TextEncoder();
    const keyMaterial = await crypto.subtle.importKey('raw', enc.encode(password), { name: 'PBKDF2' }, false, ['deriveKey']);

    return crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: enc.encode(salt),
        iterations: 100000,
        hash: 'SHA-256',
      },
      keyMaterial,
      { name: 'AES-GCM', length: 256 },
      false, // Set to false if using non-exportable keys
      ['encrypt', 'decrypt']
    );
  }

  /** Encrypt data using AES-GCM */
  async encrypt(data: string, key: CryptoKey): Promise<string> {
    const iv = crypto.getRandomValues(new Uint8Array(this.ivLength));
    const encodedData = new TextEncoder().encode(data);

    const encryptedData = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, encodedData);

    const combinedData = new Uint8Array(iv.length + encryptedData.byteLength);
    combinedData.set(iv);
    combinedData.set(new Uint8Array(encryptedData), iv.length);

    return btoa(String.fromCharCode(...combinedData));
  }

  /** Decrypt data using AES-GCM */
  async decrypt(encryptedData: string, key: CryptoKey): Promise<string> {
    const combinedData = Uint8Array.from(atob(encryptedData), (c) => c.charCodeAt(0));
    const iv = combinedData.slice(0, this.ivLength);
    const encrypted = combinedData.slice(this.ivLength);

    const decryptedData = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, encrypted);

    return new TextDecoder().decode(decryptedData);
  }
}
